import { memo, useContext, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { LeftArrowIcon, RightArrowIcon } from '../../../assets/icons/icons';
import {
  getFundingAssetKey,
  orderFundingAssets,
} from '../../../assets/services/fundingAssets';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import { getCurrencyIconUrl } from '../../../assets/utils/currencyIcon';
import BottomSheet from '../BottomSheet/BottomSheet';
import CurrencyIcon from '../CurrencyIcon/CurrencyIcon';
import './FundingAssetSheet.css';

const STAGE = {
  CURRENCY: 'currency',
  NETWORK: 'network',
};

const SCREEN_TRANSITION = {
  duration: 0.22,
  ease: [0.22, 1, 0.36, 1],
};
const STAGE_VARIANTS = {
  enter: (direction) => ({
    transform: direction > 0
      ? 'translate3d(100%, 0, 0)'
      : 'translate3d(-100%, 0, 0)',
  }),
  center: {
    transform: 'translate3d(0%, 0, 0)',
  },
  exit: (direction) => ({
    transform: direction > 0
      ? 'translate3d(-100%, 0, 0)'
      : 'translate3d(100%, 0, 0)',
  }),
};
const CURRENCY_ROW_HEIGHT = 76;
const NETWORK_ROW_HEIGHT = 64;
const SELECTED_CURRENCY_HEIGHT = 56;
const ROW_GAP = 10;
const NETWORK_LIST_MARGIN = 14;

function getListHeight(itemCount, rowHeight, gap = ROW_GAP) {
  if (itemCount <= 0) {
    return 0;
  }

  return itemCount * rowHeight + Math.max(itemCount - 1, 0) * gap;
}

function getCurrencyName(currency, language) {
  return currency?.nameTranslations?.[language]
    || currency?.nameTranslations?.en
    || currency?.name
    || currency?.asset
    || '';
}

function getCurrencyGroups(assets = []) {
  const groupsMap = new Map();

  orderFundingAssets(assets).forEach((asset) => {
    if (!asset?.currencyId) return;

    if (!groupsMap.has(asset.currencyId)) {
      groupsMap.set(asset.currencyId, {
        currencyId: asset.currencyId,
        asset: asset.asset,
        name: asset.name,
        nameTranslations: asset.nameTranslations,
        iconUrl: getCurrencyIconUrl(asset),
        networks: [],
      });
    }

    groupsMap.get(asset.currencyId).networks.push(asset);
  });

  return Array.from(groupsMap.values());
}

function AssetIcon({ src, code }) {
  return (
    <CurrencyIcon
      className="funding-asset-sheet__icon"
      imageClassName="funding-asset-sheet__icon-image"
      src={src}
      code={code}
    />
  );
}

function FundingAssetSheet({
  assets = [],
  selectedAsset,
  onClose,
  onSelect,
}) {
  const { deposit, language } = useContext(TranslationContext);
  const shouldReduceMotion = useReducedMotion();
  const currencyGroups = useMemo(() => getCurrencyGroups(assets), [assets]);
  const initialCurrency = useMemo(() => (
    currencyGroups.find((currency) => currency.currencyId === selectedAsset?.currencyId)
    || currencyGroups[0]
    || null
  ), [currencyGroups, selectedAsset]);
  const [stage, setStage] = useState(STAGE.CURRENCY);
  const [activeCurrency, setActiveCurrency] = useState(initialCurrency);
  const isNetworkStage = stage === STAGE.NETWORK;
  const stageMinHeight = useMemo(() => {
    const currencyHeight = getListHeight(currencyGroups.length, CURRENCY_ROW_HEIGHT);
    const networkRows = Math.ceil((activeCurrency?.networks?.length || 0) / 2);
    const networkHeight = SELECTED_CURRENCY_HEIGHT
      + NETWORK_LIST_MARGIN
      + getListHeight(networkRows, NETWORK_ROW_HEIGHT);

    return Math.max(currencyHeight, networkHeight);
  }, [activeCurrency?.networks?.length, currencyGroups.length]);
  const title = stage === STAGE.NETWORK
    ? deposit.selectNetwork
    : deposit.selectCurrency;

  const handleCurrencySelect = (currency) => {
    if (!currency?.networks?.length) return;

    if (currency.networks.length === 1) {
      onSelect(currency.networks[0]);
      onClose();
      return;
    }

    setActiveCurrency(currency);
    setStage(STAGE.NETWORK);
  };

  const handleNetworkSelect = (network) => {
    onSelect(network);
    onClose();
  };

  const handleBackToCurrency = () => {
    triggerImpactHaptic('light');
    setStage(STAGE.CURRENCY);
  };

  const renderCurrencyStage = () => (
    <div className="funding-asset-sheet__stage-content">
      <div className="funding-asset-sheet__list">
        {currencyGroups.map((currency) => {
          const selectedCurrency = selectedAsset?.currencyId === currency.currencyId;
          const singleNetwork = currency.networks.length === 1 ? currency.networks[0] : null;

          return (
            <button
              key={currency.currencyId}
              type="button"
              className={
                selectedCurrency
                  ? 'funding-asset-sheet__item funding-asset-sheet__item--selected'
                  : 'funding-asset-sheet__item'
              }
              onClick={() => handleCurrencySelect(currency)}
            >
              <AssetIcon src={currency.iconUrl} code={currency.asset} />
              <span className="funding-asset-sheet__copy">
                <span className="funding-asset-sheet__title">{currency.asset}</span>
                <span className="funding-asset-sheet__subtitle">
                  {singleNetwork
                    ? `${getCurrencyName(currency, language)} · ${singleNetwork.network}`
                    : getCurrencyName(currency, language)}
                </span>
              </span>
              {!selectedCurrency || !singleNetwork ? (
                <RightArrowIcon className="funding-asset-sheet__arrow-icon" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderNetworkStage = () => (
    <div className="funding-asset-sheet__stage-content">
      <div className="funding-asset-sheet__selected-currency">
        <AssetIcon src={activeCurrency?.iconUrl} code={activeCurrency?.asset} />
        <span className="funding-asset-sheet__copy">
          <span className="funding-asset-sheet__title">{activeCurrency?.asset}</span>
          <span className="funding-asset-sheet__subtitle">
            {getCurrencyName(activeCurrency, language)}
          </span>
        </span>
      </div>

      <div className="funding-asset-sheet__list funding-asset-sheet__list--networks">
        {(activeCurrency?.networks || []).map((network) => {
          const selectedNetwork = getFundingAssetKey(network) === getFundingAssetKey(selectedAsset);

          return (
            <button
              key={getFundingAssetKey(network)}
              type="button"
              className={
                selectedNetwork
                  ? 'funding-asset-sheet__item funding-asset-sheet__item--selected'
                  : 'funding-asset-sheet__item'
              }
              onClick={() => handleNetworkSelect(network)}
            >
              <span className="funding-asset-sheet__copy">
                <span className="funding-asset-sheet__title">{network.network}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <BottomSheet
      className="funding-asset-sheet"
      panelClassName="funding-asset-sheet__panel"
      onClose={onClose}
    >
      <div className="funding-asset-sheet__header">
        {stage === STAGE.NETWORK ? (
          <button
            type="button"
            className="funding-asset-sheet__back"
            aria-label={deposit.selectCurrency}
            onClick={handleBackToCurrency}
          >
            <LeftArrowIcon className="funding-asset-sheet__back-icon" />
          </button>
        ) : (
          <span className="funding-asset-sheet__back-spacer" aria-hidden="true" />
        )}
        <h2 className="funding-asset-sheet__heading">{title}</h2>
        <span className="funding-asset-sheet__back-spacer" aria-hidden="true" />
      </div>

      <div
        className="funding-asset-sheet__stage-window"
        style={{ '--funding-asset-stage-min-height': `${stageMinHeight}px` }}
      >
        <AnimatePresence initial={false} custom={isNetworkStage ? 1 : -1}>
          <motion.div
            key={stage}
            className="funding-asset-sheet__stage"
            custom={isNetworkStage ? 1 : -1}
            variants={STAGE_VARIANTS}
            initial={shouldReduceMotion ? false : 'enter'}
            animate="center"
            exit={shouldReduceMotion ? undefined : 'exit'}
            transition={shouldReduceMotion ? { duration: 0 } : SCREEN_TRANSITION}
          >
            {isNetworkStage ? renderNetworkStage() : renderCurrencyStage()}
          </motion.div>
        </AnimatePresence>
      </div>
    </BottomSheet>
  );
}

export default memo(FundingAssetSheet);
